import React, { useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router'
import { DynamakerApplication } from '@augmatica/iframe-interface-react'

import { FULFILLED } from '../constants/constants'
import { setIsSummaryPanelVisible, setIsSummaryPreviewExpanded } from '../store/ui/uiSlice'
import OverlayControls from '../components/OverlayControls/OverlayControls'
import ToolbarContainer from '../components/Toolbar/ToolbarContainer'
import SummaryPanel from '../components/SummaryPanel/SummaryPanel'

import * as Styled from './styles'
import { useAppDispatch, useAppSelector } from '../hooks'

const CONFIGURATOR_VIEW_ID = 'configurator-view'

const baseRoute = window.location.pathname
const withTrailingSlash = baseRoute.endsWith('/')

const usePreserveQueryParamsNavigate = () => {
  const navigate = useNavigate()
  const { search } = useLocation()

  return (path: string) => {
    navigate(path + search)
  }
}

const Routing: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = usePreserveQueryParamsNavigate()
  const { pathname } = useLocation()

  const { isSummaryPanelVisible } = useAppSelector((state) => state.ui)

  useEffect(() => {
    navigate(
      isSummaryPanelVisible
        ? `${baseRoute}${withTrailingSlash ? '' : '/'}summary${withTrailingSlash ? '/' : ''}`
        : `${baseRoute}`
    )
  }, [isSummaryPanelVisible])

  // Handle back in browser button
  useEffect(() => {
    if (pathname === baseRoute) {
      dispatch(setIsSummaryPanelVisible(false))
    }
  }, [pathname])

  return null
}

interface Props {
  forceDmTestEnv?: boolean
  enableDmTestTab?: boolean
  awaitProjectLoaded?: boolean
  isIframeReady: boolean
  onRequestSaveProject: any // TODO type correctly
}

const ConfiguratorView: React.FC<Props> = ({
  forceDmTestEnv = false,
  enableDmTestTab = false,
  awaitProjectLoaded = false,
  isIframeReady,
  onRequestSaveProject,
}) => {
  const dispatch = useAppDispatch()

  const { content, marketCode } = useAppSelector((state) => state.init)
  const { requestStatus_fetchPrices_aggregated } = useAppSelector((state) => state.requests.requestStatus)
  const { isSummaryPreviewExpanded, isSummaryPanelVisible } = useAppSelector((state) => state.ui)

  return (
    <>
      <Styled.ConfiguratorView
        id={CONFIGURATOR_VIEW_ID}
        isSummaryPreviewExpanded={isSummaryPreviewExpanded}
        $insideSummaryPanel={isSummaryPanelVisible}
        onClick={(event: any) => {
          if (isSummaryPreviewExpanded && event.target.id === CONFIGURATOR_VIEW_ID) {
            dispatch(setIsSummaryPreviewExpanded(false))
          }
        }}
      >
        <Styled.IframeContainer $insideSummaryPanel={isSummaryPanelVisible}>
          {isSummaryPanelVisible && (
            <Styled.SummaryHeader level={1}>{content.summary_panel.title}</Styled.SummaryHeader>
          )}

          <DynamakerApplication
            appId={import.meta.env.VITE_DM_APP_ID}
            production={import.meta.env.VITE_SCENSUM_ENV === 'production' && !forceDmTestEnv}
            args={{
              marketCode,
              awaitProjectLoaded,
              ...(enableDmTestTab ? { test: true } : { 'external-ui': true }),
            }}
          />

          {isIframeReady && !isSummaryPanelVisible && <OverlayControls />}

          {!isIframeReady && !isSummaryPanelVisible && (
            <div className="iframe-loader">
              <div className="split-cube" />
              <div className="text">{content.other.loading}</div>
            </div>
          )}
        </Styled.IframeContainer>

        <BrowserRouter>
          <Routes>
            <Route path={baseRoute} element={<ToolbarContainer />} />
            <Route
              path={`${baseRoute}${withTrailingSlash ? '' : '/'}summary${withTrailingSlash ? '/' : ''}`}
              element={
                requestStatus_fetchPrices_aggregated === FULFILLED ? (
                  <SummaryPanel onRequestSaveProject={onRequestSaveProject} />
                ) : null
              }
            />
          </Routes>
          <Routing />
        </BrowserRouter>
      </Styled.ConfiguratorView>
    </>
  )
}

export default ConfiguratorView
